import React, { useState, useEffect } from "react";
import { Play, Video, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { UserGalleryService } from "../services/UserGalleryServices";

export default function Videos() {
    const [videoItems, setVideoItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchVideos();
    }, []);

    const [activeVideo, setActiveVideo] = useState(null);

    const getYouTubeID = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url?.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const fetchVideos = async () => {
        try {
            const data = await UserGalleryService.getVideos();
            // Assign random rotations for consistent gallery look
            const rotations = ["rotate-1", "-rotate-1", "rotate-2", "-rotate-2", "rotate-3", "-rotate-3"];
            const itemsWithRotation = data.map(item => ({
                ...item,
                rotation: rotations[Math.floor(Math.random() * rotations.length)]
            }));
            setVideoItems(itemsWithRotation);
        } catch (err) {
            console.error("Failed to fetch gallery videos:", err);
            setError("Unable to load the video gallery at this time.");
        } finally {
            setLoading(false);
        }
    };


    const LoadingSkeleton = () => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 px-4">
            {[1, 2, 3].map((i) => (
                <div key={i} className={`transform ${i % 2 === 0 ? 'rotate-1' : '-rotate-1'}`}>
                    <div className="bg-white p-4 pb-12 shadow-lg border border-black/10 animate-pulse">
                        <div className="aspect-video bg-gray-200 mb-4" />
                        <div className="h-4 w-3/4 bg-gray-200 mx-auto rounded" />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-white">
            <SEO title="Video Performances" description="Watch concert performances, music videos, and video highlights of Temidayo Balogun." url="/gallery/videos" />
            <JazzHeader />

            {/* Video Modal */}
            <AnimatePresence>
                {activeVideo && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/95 backdrop-blur-sm"
                            onClick={() => setActiveVideo(null)}
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="relative w-full max-w-5xl aspect-video bg-black shadow-2xl rounded-2xl overflow-hidden"
                        >
                            <button
                                onClick={() => setActiveVideo(null)}
                                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all shadow-lg backdrop-blur-md"
                            >
                                <X size={20} />
                            </button>
                            {activeVideo.youtube_link ? (
                                <iframe
                                    src={`https://www.youtube.com/embed/${getYouTubeID(activeVideo.youtube_link)}?autoplay=1&controls=1&rel=0&modestbranding=1&fs=0&disablekb=1&playsinline=1&iv_load_policy=3`}
                                    className="w-full h-full"
                                    title={activeVideo.caption}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            ) : (
                                <video
                                    src={activeVideo.file_path}
                                    controls
                                    className="w-full h-full"
                                />
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">

                    {/* Header */}
                    <div className="mb-16 sm:mb-24 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal text-black mb-6"
                            style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                            Video Gallery
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto mb-8" />
                        <p
                            className="max-w-2xl mx-auto text-black font-medium text-lg leading-relaxed font-serif italic"
                            style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                        >
                            Watch live performances, behind-the-scenes footage, and exclusive interviews.
                        </p>
                    </div>

                    {/* Grid / Content */}
                    <div className="min-h-[600px]">
                        {loading ? (
                            <LoadingSkeleton />
                        ) : error ? (
                            <div className="text-center py-20 px-6 bg-[#FAFAFA] rounded-3xl border border-dashed border-black/20">
                                <Video size={48} className="mx-auto text-black/40 mb-4" />
                                <p className="text-black font-serif italic text-lg leading-relaxed">{error}</p>
                            </div>
                        ) : videoItems.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 px-4">
                                {videoItems.map((item, index) => (
                                    <div
                                        key={item.id || index}
                                        className={`transform transition-all duration-500 hover:scale-105 hover:rotate-0 hover:z-10 ${item.rotation}`}
                                        onClick={() => setActiveVideo(item)}
                                    >
                                        <div className="bg-white p-4 pb-12 shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-black/5 h-full flex flex-col group cursor-pointer">
                                            <div className="relative aspect-video overflow-hidden bg-black mb-4">
                                                {/* Thumbnail logic */}
                                                {item.youtube_link ? (
                                                    <img
                                                        src={`https://img.youtube.com/vi/${getYouTubeID(item.youtube_link)}/maxresdefault.jpg`}
                                                        alt={item.caption}
                                                        className="w-full h-full object-cover transition-opacity"
                                                        onError={(e) => {
                                                            if (e.target.src.includes('maxresdefault')) {
                                                                e.target.src = e.target.src.replace('maxresdefault', 'hqdefault');
                                                            } else {
                                                                e.target.src = "https://via.placeholder.com/800x450?text=Video+Preview";
                                                            }
                                                        }}
                                                    />
                                                ) : item.file_path ? (
                                                    <video
                                                        src={item.file_path}
                                                        poster={item.file_path.replace('.mp4', '.jpg')}
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                        className="w-full h-full object-cover transition-opacity"
                                                        onMouseEnter={(e) => { try { e.target.play(); } catch { } }}
                                                        onMouseLeave={(e) => { try { e.target.pause(); e.target.currentTime = 0; } catch { } }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-black/90 flex items-center justify-center">
                                                        <Video size={48} className="text-white/10" />
                                                    </div>
                                                )}

                                                {/* Play Button Overlay */}
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                                                        <Play size={24} fill="black" className="ml-1 text-black" />
                                                    </div>
                                                </div>
                                            </div>

                                            <p
                                                className="text-center text-black text-lg mt-auto group-hover:text-jazz-brown transition-colors"
                                                style={{ fontFamily: "Gloria Hallelujah, cursive" }}
                                            >
                                                {item.caption}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20 border border-dashed border-black/20 rounded-3xl bg-[#FAFAFA]">
                                <Video size={48} className="mx-auto text-black/30 mb-4" />
                                <p className="text-black font-serif italic text-lg leading-relaxed">No videos available at the moment.</p>
                            </div>
                        )}
                    </div>

                </div>
            </main>

            <JazzFooter />

            <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Gloria+Hallelujah&display=swap');
      `}</style>
        </div>
    );
}
